import React, { useEffect, useMemo, useState } from "react";
import { api, useLang, formatCurrency, formatApiError } from "../lib/providers";
import { PageHeader, Btn, EmptyState, TextInput } from "../components/primitives";
import { downloadPdfBundle } from "../lib/bundle";
import { MagnifyingGlass, FilePdf, Trash, DotsSixVertical, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { Checkbox } from "../components/BulkActionBar";
import { toast } from "sonner";

/**
 * Bundle-Builder: kombiniert beliebig viele Angebote, Aufmaße und Rechnungen
 * (auch gemischt) in EIN PDF. Reihenfolge kann per Pfeiltasten angepasst
 * werden. Originale bleiben unverändert.
 */
export default function PdfBundlePage() {
  const { lang } = useLang();
  const [tab, setTab] = useState("quote");
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [aufmasse, setAufmasse] = useState([]);
  const [q, setQ] = useState("");
  const [selection, setSelection] = useState([]); // [{type, id, label, sub}]
  const [busy, setBusy] = useState(false);
  const [filename, setFilename] = useState(`Sammlung_${new Date().toISOString().slice(0, 10)}`);

  useEffect(() => {
    (async () => {
      try {
        const [qr, ir, ar] = await Promise.all([
          api.get("/quotes"),
          api.get("/invoices"),
          api.get("/aufmasse"),
        ]);
        setQuotes(qr.data || []);
        setInvoices(ir.data || []);
        setAufmasse(ar.data || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      }
    })();
  }, []);

  const source = useMemo(() => {
    const map = { quote: quotes, invoice: invoices, aufmass: aufmasse };
    const list = map[tab] || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((x) => {
      const cust = x.customer_snapshot || {};
      const hay = [
        x.number, x.title, cust.company, cust.name, x.notes,
      ].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [tab, quotes, invoices, aufmasse, q]);

  const isInSelection = (type, id) => selection.some((s) => s.type === type && s.id === id);
  const toggle = (type, id, item) => {
    setSelection((prev) => {
      const exists = prev.findIndex((s) => s.type === type && s.id === id);
      if (exists >= 0) return prev.filter((_, i) => i !== exists);
      const label = item.number || item.id.slice(0, 6);
      const cust = item.customer_snapshot || {};
      const sub = [cust.company || cust.name, item.title].filter(Boolean).join(" · ");
      return [...prev, { type, id, label, sub }];
    });
  };

  const move = (idx, dir) => {
    setSelection((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return next;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const removeAt = (idx) => setSelection((prev) => prev.filter((_, i) => i !== idx));
  const clearAll = () => setSelection([]);

  const build = async () => {
    if (selection.length === 0) return;
    setBusy(true);
    try {
      const items = selection.map((s) => ({ type: s.type, id: s.id }));
      await downloadPdfBundle(items, filename, lang);
    } finally {
      setBusy(false);
    }
  };

  const tabs = [
    { k: "quote", label: lang === "de" ? "Angebote" : "Quotes", n: quotes.length, prefix: "A" },
    { k: "aufmass", label: lang === "de" ? "Aufmaße" : "Measurements", n: aufmasse.length, prefix: "AM" },
    { k: "invoice", label: lang === "de" ? "Rechnungen" : "Invoices", n: invoices.length, prefix: "RSI" },
  ];

  const typeBadge = {
    quote: { label: lang === "de" ? "Angebot" : "Quote", cls: "bg-[var(--brand)]/10 text-[var(--brand)]" },
    aufmass: { label: lang === "de" ? "Aufmaß" : "Measurement", cls: "bg-[#8B5CF6]/10 text-[#8B5CF6]" },
    invoice: { label: lang === "de" ? "Rechnung" : "Invoice", cls: "bg-[#10B981]/10 text-[#10B981]" },
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="05 / Bundle"
        title={lang === "de" ? "PDF-Sammlung erstellen" : "Build PDF bundle"}
        actions={
          <div className="flex gap-2 items-center">
            <TextInput
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="Sammlung"
              className="w-56"
              data-testid="bundle-filename"
            />
            <Btn onClick={build} disabled={busy || selection.length === 0} data-testid="bundle-build">
              <FilePdf size={16} /> {busy
                ? (lang === "de" ? "Erstelle…" : "Building…")
                : (lang === "de" ? `PDF erstellen (${selection.length})` : `Build PDF (${selection.length})`)}
            </Btn>
          </div>
        }
      />

      <p className="text-sm text-[var(--muted)] mb-6 max-w-3xl">
        {lang === "de"
          ? "Wähle beliebig viele Angebote, Aufmaße und Rechnungen aus – auch typenübergreifend. Die Reihenfolge in der Auswahlliste rechts entspricht der Reihenfolge im finalen PDF."
          : "Pick any quotes, measurement protocols and invoices — mix types freely. The order in the right-hand selection list is the order in the final PDF."}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Source list */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex gap-2 border-b border-[var(--border)]">
            {tabs.map((tt) => (
              <button
                key={tt.k}
                onClick={() => setTab(tt.k)}
                data-testid={`bundle-tab-${tt.k}`}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === tt.k ? "border-[var(--brand)] text-[var(--ink)]" : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"}`}
              >
                {tt.label} <span className="text-[var(--muted-2)] tabular-nums">({tt.n})</span>
              </button>
            ))}
          </div>

          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
            <TextInput
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={lang === "de" ? "Suche nach Nummer, Titel, Kunde…" : "Search by number, title, customer…"}
              className="pl-9"
              data-testid="bundle-search"
            />
          </div>

          {source.length === 0 ? (
            <EmptyState title={lang === "de" ? "Keine Einträge" : "No entries"} />
          ) : (
            <div className="swiss-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg)] text-[var(--muted)]">
                  <tr>
                    <th className="text-left font-medium px-3 py-2 label-overline w-10"></th>
                    <th className="text-left font-medium px-3 py-2 label-overline">Nr.</th>
                    <th className="text-left font-medium px-3 py-2 label-overline">{lang === "de" ? "Titel / Kunde" : "Title / Customer"}</th>
                    <th className="text-right font-medium px-3 py-2 label-overline">{lang === "de" ? "Summe" : "Total"}</th>
                  </tr>
                </thead>
                <tbody>
                  {source.slice(0, 200).map((x) => {
                    const cust = x.customer_snapshot || {};
                    const checked = isInSelection(tab, x.id);
                    return (
                      <tr
                        key={x.id}
                        className={`border-t border-[var(--border)] transition-colors cursor-pointer ${checked ? "bg-[var(--brand)]/[0.05]" : "hover:bg-[var(--row-hover)]"}`}
                        onClick={() => toggle(tab, x.id, x)}
                        data-testid={`bundle-source-${tab}-${x.id}`}
                      >
                        <td className="px-3 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={checked} onChange={() => toggle(tab, x.id, x)} testId={`bundle-check-${tab}-${x.id}`} />
                        </td>
                        <td className="px-3 py-2 font-mono tabular-nums text-[var(--muted)]">{x.number}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-[var(--ink)] truncate flex items-center gap-2">
                            <span className="truncate">{x.title || "—"}</span>
                            {tab === "aufmass" && x.has_signed_pdf && (
                              <span
                                title={lang === "de" ? "Signiertes PDF hinterlegt" : "Signed PDF attached"}
                                className="shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-[var(--brand)]/10 text-[var(--brand)] uppercase tracking-wide"
                                data-testid={`bundle-signed-${x.id}`}
                              >
                                {lang === "de" ? "Signiert" : "Signed"}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[var(--muted-2)] truncate">{cust.company || cust.name || "—"}</div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-[var(--muted)]">
                          {x.total != null ? formatCurrency(x.total, lang) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Selection list */}
        <aside className="swiss-card p-4 lg:sticky lg:top-4 lg:self-start" data-testid="bundle-selection">
          <div className="flex items-center justify-between mb-3">
            <div className="label-overline">{lang === "de" ? "Auswahl" : "Selection"}</div>
            {selection.length > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-[var(--muted)] hover:text-[var(--ink)]"
                data-testid="bundle-clear-all"
              >
                {lang === "de" ? "leeren" : "clear"}
              </button>
            )}
          </div>
          {selection.length === 0 ? (
            <p className="text-sm text-[var(--muted-2)] py-8 text-center">
              {lang === "de" ? "Noch nichts gewählt." : "Nothing selected yet."}
            </p>
          ) : (
            <ul className="space-y-1">
              {selection.map((s, idx) => {
                const badge = typeBadge[s.type];
                return (
                  <li
                    key={`${s.type}-${s.id}`}
                    className="flex items-start gap-2 border border-[var(--border)] rounded-sm px-2 py-2"
                    data-testid={`bundle-selected-${idx}`}
                  >
                    <DotsSixVertical size={14} className="text-[var(--muted-2)] mt-1 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-wide ${badge.cls}`}>{badge.label}</span>
                        <span className="font-mono text-xs text-[var(--muted)] truncate">{s.label}</span>
                      </div>
                      {s.sub && <div className="text-xs text-[var(--muted-2)] truncate mt-0.5">{s.sub}</div>}
                    </div>
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[var(--muted-2)] hover:text-[var(--ink)] disabled:opacity-30" data-testid={`bundle-up-${idx}`} title="Nach oben"><ArrowUp size={12} /></button>
                      <button onClick={() => move(idx, +1)} disabled={idx === selection.length - 1} className="text-[var(--muted-2)] hover:text-[var(--ink)] disabled:opacity-30" data-testid={`bundle-down-${idx}`} title="Nach unten"><ArrowDown size={12} /></button>
                    </div>
                    <button onClick={() => removeAt(idx)} className="text-[var(--muted-2)] hover:text-[#FF3B30]" data-testid={`bundle-remove-${idx}`}>
                      <Trash size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
